/**
 * Error Message Mapping
 *
 * Maps HTTP status codes to user-friendly error messages.
 */

/**
 * Map HTTP status code to user-friendly message.
 *
 * @param status - HTTP status code (0 for network errors)
 * @param networkError - Optional network error message
 * @returns User-friendly error message
 */
export function mapErrorToUserMessage(status: number, networkError?: string): string {
  switch (status) {
    case 0:
      // Network error
      if (networkError?.includes('timeout')) {
        return 'Request timed out. Please check your connection and try again.';
      }
      if (networkError?.includes('abort')) {
        return 'Request was cancelled.';
      }
      return 'Unable to connect. Please check your internet connection.';

    case 400:
      return 'Invalid request. Please check your input and try again.';

    case 401:
      return 'Your session has expired. Please sign in again.';

    case 403:
      return "You don't have permission to access this resource.";

    case 404:
      return 'The requested resource was not found.';

    case 408:
      return 'Request timed out. Please try again.';

    case 429:
      return 'Too many requests. Please wait a moment and try again.';

    case 500:
      return 'Something went wrong on our end. Please try again later.';

    case 502:
      return 'Service temporarily unavailable. Please try again later.';

    case 503:
      return 'Service is currently unavailable. Please try again later.';

    case 504:
      return 'Request timed out. Please try again later.';

    default:
      if (status >= 500) {
        return 'Something went wrong. Please try again later.';
      }
      if (status >= 400) {
        return 'There was a problem with your request.';
      }
      return 'An unexpected error occurred.';
  }
}

/**
 * Check if an error is a network connectivity issue.
 */
export function isNetworkError(status: number): boolean {
  return status === 0;
}

/**
 * Check if an error is an authentication issue.
 */
export function isAuthError(status: number): boolean {
  return status === 401 || status === 403;
}

/**
 * Check if an error is a server issue (potentially transient).
 */
export function isServerError(status: number): boolean {
  return status >= 500;
}

/**
 * Get suggested action for an error.
 */
export function getSuggestedAction(status: number): string {
  if (status === 401) {
    return 'Sign in again';
  }
  if (status === 403) {
    return 'Contact administrator';
  }
  if (status === 0 || status >= 500) {
    return 'Try again';
  }
  return '';
}
